import React, { useEffect, useState } from "react";
import {
  fetchTransactions,
  fetchBranches,
  deleteTransaction,
} from "../utils/api";
import {
  Table,
  TableBody,
  TableFooter,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Box,
  Container,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getUser } from "../utils/auth";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import handlePrint from "../utils/print";

const DashboardPage = () => {
  const navigate = useNavigate();
  const user = getUser();

  const [branches, setBranches] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filters, setFilters] = useState({
    startDate:
      user?.username?.includes("am") ||
      (user?.username?.includes("pm") && new Date().getHours() >= 19) ||
      (user?.username?.includes("ad") && new Date().getHours() >= 7)
        ? new Date().toLocaleDateString("sv-SE") // Today
        : (() => {
            const now = new Date();
            now.setDate(now.getDate() - 1); // Yesterday
            return now.toLocaleDateString("sv-SE");
          })(),
    startTime: user?.username?.includes("pm") ? "19:00" : "07:00",
    endDate:
      (user?.username?.includes("pm") && new Date().getHours() >= 19) ||
      (user?.username?.includes("ad") && new Date().getHours() >= 7)
        ? (() => {
            const now = new Date();
            now.setDate(now.getDate() + 1); // Tomorrow
            return now.toLocaleDateString("sv-SE");
          })()
        : new Date().toLocaleDateString("sv-SE"), // Default to today
    endTime: user?.username?.includes("am") ? "20:00" : "07:00",
    branch: "",
    shift: "",
    user: "",
    page: 1,
    limit: 500,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [totalSum, setTotalSum] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registerDialog, setRegisterDialog] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleOpenSnackbar = () => setSnackbarOpen(true);
  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handleOpenRegisterDialog = () => setRegisterDialogOpen(true);
  const handleCloseRegisterDialog = () => setRegisterDialogOpen(false);

  const handleChangePage = (event, newPage, newRowsPerPage) => {
    setPage(newPage);
    setFilters((prev) => ({
      ...prev,
      page: newPage + 1,
      limit: newRowsPerPage || rowsPerPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    handleChangePage(event, 0, newRowsPerPage);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchBranches();
        setBranches(data.data);
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    };

    if (user.role === "admin") {
      fetchData();
    }
  }, []);

  useEffect(() => {
    fetchTransactionsData();
  }, [filters]);

  const fetchTransactionsData = async () => {
    setLoading(true);
    try {
      const data = await fetchTransactions(filters);
      setTransactions(data.data);
      calculateTotal(data.data);
      setTotalRows(data.totalCount);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (transactions) => {
    const total = transactions.reduce(
      (sum, transaction) =>
        sum +
        transaction.ws +
        transaction.dr +
        transaction.fd +
        transaction.dtPrice * transaction.dtQty +
        transaction.fcPrice * transaction.fcQty +
        transaction.blPrice * transaction.blQty +
        transaction.sp +
        transaction.ad +
        transaction.sw,
      0
    );
    setTotalSum(total);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === "branch" && !value) filters.shift = "";
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNavigateToAddTransaction = () => {
    navigate("/add-transaction");
  };

  const handleDeleteConfirm = (transaction) => {
    setRegisterDialog(
      <>
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to delete this transaction?
        </DialogTitle>
        <DialogContent>
          This action will also restore the products used to the inventory, and
          the gas (if any).
          <br />
          <br />
          <Typography variant="caption">
            If you encountered an issue, please try again later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegisterDialog}>Cancel</Button>
          <Button onClick={() => handleDelete(transaction)} autoFocus>
            Continue
          </Button>
        </DialogActions>
      </>
    );

    handleOpenRegisterDialog();
  };

  const handleDelete = async (transaction) => {
    try {
      const response = await deleteTransaction({ transaction });
      if (response.status === 200) {
        setSnackbarMessage(response.data.message || "Successful");
        setSnackbarSeverity("success");
        handleOpenSnackbar();
        handleCloseRegisterDialog();
        fetchTransactionsData();
      } else {
        setSnackbarMessage(response.data.message || "Failed");
        setSnackbarSeverity("error");
        handleOpenSnackbar();
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage(
        error?.response?.data?.message ||
          "An error occurred. Please try again later."
      );
      setSnackbarSeverity("error");
      handleOpenSnackbar();
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ padding: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={2}
        >
          <Typography variant="h5">Transactions</Typography>
          {user.role === "user" && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNavigateToAddTransaction}
            >
              Add Transaction
            </Button>
          )}
        </Box>
        {/* Filters */}
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            label="Start Date"
            type="date"
            name="startDate"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            value={filters.startDate}
            onChange={handleFilterChange}
          />
          <TextField
            size="small"
            label="Time"
            type="time"
            name="startTime"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            value={filters.startTime}
            onChange={handleFilterChange}
          />
          <TextField
            size="small"
            label="End Date"
            type="date"
            name="endDate"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            value={filters.endDate}
            onChange={handleFilterChange}
          />
          <TextField
            size="small"
            label="Time"
            type="time"
            name="endTime"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            value={filters.endTime}
            onChange={handleFilterChange}
          />
          {user.role === "admin" && (
            <>
              <FormControl sx={{ flex: 1 }} size="small">
                <InputLabel id="br-select-label">Branch</InputLabel>
                <Select
                  labelId="br-select-label"
                  id="br-select"
                  name="branch"
                  label="Branch"
                  variant="outlined"
                  value={filters.branch}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">
                    <em>-</em>
                  </MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.nm}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }} size="small">
                <InputLabel id="sh-select-label">Shift</InputLabel>
                <Select
                  labelId="sh-select-label"
                  id="sh-select"
                  name="shift"
                  label="Shift"
                  variant="outlined"
                  value={filters.shift}
                  onChange={handleFilterChange}
                  disabled={!filters.branch}
                >
                  <MenuItem value="">
                    <em>-</em>
                  </MenuItem>
                  <MenuItem value="1">AM</MenuItem>
                  <MenuItem value="2">PM</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="User"
                name="user"
                value={filters.user}
                onChange={handleFilterChange}
                sx={{ display: "none" }}
              />
              <Button
                variant="contained"
                onClick={() => setFilters((prev) => ({ ...prev, page: 1 }))}
                sx={{ display: "none" }}
              >
                Apply Filters
              </Button>
            </>
          )}
        </div>
        {/* Transactions Table */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>#</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Tran#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Wt(kg)</TableCell>
                <TableCell>Wash</TableCell>
                <TableCell>Super Wash</TableCell>
                <TableCell>Spin</TableCell>
                <TableCell>Dry</TableCell>
                <TableCell>Add Dry</TableCell>
                <TableCell>Fold</TableCell>
                <TableCell>Det.</TableCell>
                <TableCell>Fabcon.</TableCell>
                <TableCell>Bleach</TableCell>
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((txn, index) => (
                <TableRow key={txn._id}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {user.role === "admin" && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteConfirm(txn._id)}
                        title="Delete transaction"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                    {user.role === "user" && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handlePrint(txn)}
                        title="Reprint"
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{rowsPerPage * page + index + 1}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {txn.br.nm}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {new Date(txn.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {txn.usr?.fullname || "-"}
                  </TableCell>
                  <TableCell>{txn.tx}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{txn.nm}</TableCell>
                  <TableCell>{txn.wt}</TableCell>
                  <TableCell>{txn.ws}</TableCell>
                  <TableCell>{txn.sw}</TableCell>
                  <TableCell>{txn.sp}</TableCell>
                  <TableCell>{txn.dr}</TableCell>
                  <TableCell>{txn.ad}</TableCell>
                  <TableCell>{txn.fd}</TableCell>
                  <TableCell>{txn.dtPrice * txn.dtQty}</TableCell>
                  <TableCell>{txn.fcPrice * txn.fcQty}</TableCell>
                  <TableCell>{txn.blPrice * txn.blQty}</TableCell>
                  <TableCell>
                    {txn.ws +
                      txn.dr +
                      txn.fd +
                      txn.dtPrice * txn.dtQty +
                      txn.fcPrice * txn.fcQty +
                      txn.blPrice * txn.blQty +
                      txn.sp +
                      txn.ad +
                      txn.sw}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && transactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={18} align="center">
                    No Transactions Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={17} align="right">
                  <strong>Total Sum:</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalSum.toFixed(2)}</strong>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[500, 700, 1000]}
          component="div"
          count={totalRows}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        {/* Loading Indicator */}
        {loading && <p align="center">Loading...</p>}
      </Container>
      <Dialog
        open={registerDialogOpen}
        onClose={handleCloseRegisterDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {registerDialog}
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DashboardPage;
